import { Link } from "react-router-dom";
import "./Favourite.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import VivahApi from "../../api";
import { useEffect, useState, useCallback } from "react";

const PAGE_COUNT = 8;
const PLACEHOLDER = "assets/images/avatar-placeholder.svg";

function SkeletonCard() {
  return (
    <div className="fav-card fav-skeleton">
      <div className="fav-sk-photo" />
      <div className="fav-sk-body">
        <div className="fav-sk-line fav-sk-name" />
        <div className="fav-sk-line fav-sk-sub" />
        <div className="fav-sk-line fav-sk-sub" />
        <div className="fav-sk-line fav-sk-sub" />
      </div>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fav-toast fav-toast--${type}`}>
      <i className={`ti ${type === "success" ? "ti-check" : "ti-close"}`} />
      {message}
    </div>
  );
}

function Favourite() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [removing, setRemoving] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const res = await VivahApi.Favourite.listFavourite();
        setAll(res.data.data || []);
      } catch {
        showToast("Failed to load favourites.", "error");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [showToast]);

  const totalPage = Math.ceil(all.length / PAGE_COUNT);
  const paginated = all.slice((page - 1) * PAGE_COUNT, page * PAGE_COUNT);
  const fromResult = all.length === 0 ? 0 : (page - 1) * PAGE_COUNT + 1;
  const toResult = Math.min(page * PAGE_COUNT, all.length);

  const handleRemove = async (favouriteId, profileId) => {
    setRemoving((r) => ({ ...r, [favouriteId]: true }));
    // optimistic
    setAll((prev) => prev.filter((f) => f.favouriteId !== favouriteId));
    if (page > Math.ceil((all.length - 1) / PAGE_COUNT)) setPage((p) => Math.max(1, p - 1));
    try {
      await VivahApi.Favourite.removeFavourite(profileId);
      showToast("Removed from favourites.", "info");
    } catch {
      // revert on failure by reloading
      showToast("Could not remove. Please try again.", "error");
      const res = await VivahApi.Favourite.listFavourite();
      setAll(res.data.data || []);
    } finally {
      setRemoving((r) => ({ ...r, [favouriteId]: false }));
    }
  };
  return (
    <>
      <div className="gla_page" id="gla_page">
        <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
        <Header />

        {/* <div className="gla_page_title gla_image_bck gla_wht_txt" data-color="#282828">
          <div className="container text-left">
            <div className="row">
              <div className="col-md-8">
                <h1 className="gla_h1_title">My Favourites</h1>
              </div>
              <div className="col-md-4">
                <div className="breadcrumbs">
                  <a href="#">Home</a><span>My Favourites</span>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        <section id="gla_content" className="gla_content page-top-offset">
          <section className="gla_section">
            <div className="container">

              {/* Header row */}
              {!loading && all.length > 0 && (
                <div className="fav-header">
                  <span className="fav-count">
                    Showing {fromResult}–{toResult} of {all.length} saved
                  </span>
                </div>
              )}

              {/* Grid */}
              <div className="fav-grid">
                {loading ? (
                  Array.from({ length: PAGE_COUNT }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))
                ) : paginated.length === 0 ? (
                  <div className="fav-empty">
                    <i className="ti ti-heart" />
                    <p>You haven't saved any profiles yet.</p>
                    <Link className="fav-empty-cta" to="/search">Browse Matches</Link>
                  </div>
                ) : (
                  paginated.map((p) => (
                    <div className="fav-card" key={p.favouriteId}>
                      <Link
                        className="fav-card-photo-wrap"
                        to={`/profile/${p.profile?.sid || p.profileId}`}
                      >
                        <img
                          className="fav-card-photo"
                          src={p.profile?.photo || PLACEHOLDER}
                          alt={p.profile?.name || "Profile"}
                          onError={(ev) => { ev.target.src = PLACEHOLDER; }}
                        />
                      </Link>

                      <div className="fav-card-body">
                        <Link
                          className="fav-card-name"
                          to={`/profile/${p.profile?.sid || p.profileId}`}
                        >
                          {p.profile?.name || "Unknown"}
                        </Link>
                        <span className="fav-card-age">
                          {p.profile?.age ? `${p.profile.age} yrs` : ""}
                        </span>

                        <div className="fav-card-chips">
                          {p.profile?.religion && (
                            <span className="fcc">
                              <i className="ti ti-direction-alt" />{p.profile.religion}
                            </span>
                          )}
                          {(p.profile?.city || p.profile?.state) && (
                            <span className="fcc">
                              <i className="ti ti-location-pin" />{p.profile.city || p.profile.state}
                            </span>
                          )}
                          {p.profile?.education && (
                            <span className="fcc">
                              <i className="ti ti-shield" />{p.profile.education}
                            </span>
                          )}
                          {p.profile?.occupation && (
                            <span className="fcc">
                              <i className="ti ti-medall" />{p.profile.occupation}
                            </span>
                          )}
                        </div>

                        <div className="fav-card-actions">
                          <Link
                            className="fca-view"
                            to={`/profile/${p.profile?.sid || p.profileId}`}
                          >
                            <i className="ti ti-eye" /> View
                          </Link>
                          <button
                            className="fca-remove"
                            disabled={removing[p.favouriteId]}
                            onClick={() => handleRemove(p.favouriteId, p.profileId)}
                            title="Remove from favourites"
                          >
                            <i className="ti ti-heart-broken" /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {!loading && totalPage > 1 && (
                <div className="fav-pagination">
                  <button
                    className="fpag-btn"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <i className="ti ti-angle-left" /> Prev
                  </button>
                  <div className="fpag-pages">
                    {Array.from({ length: totalPage }).map((_, i) => (
                      <button
                        key={i}
                        className={`fpag-page${page === i + 1 ? " active" : ""}`}
                        onClick={() => setPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    className="fpag-btn"
                    disabled={page === totalPage}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next <i className="ti ti-angle-right" />
                  </button>
                </div>
              )}

            </div>
          </section>
        </section>

        <Footer />
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

export default Favourite;
